import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import { CreditBalance, CreditPackCard } from '../components/credits';
import { Card, Spinner, SectionHeader } from '../components/common';
import { CreditPack } from '../types';
import { creditsApi } from '../api/credits';

export function CreditsPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();

  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const purchaseSuccess = searchParams.get('success') === 'true';
  const purchaseCanceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const data = await creditsApi.getPacks();
        setPacks(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load credit packs';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, []);

  useEffect(() => {
    // Refresh user data if purchase was successful to update credit balance
    if (purchaseSuccess) {
      refreshUser();
    }
  }, [purchaseSuccess, refreshUser]);

  const handlePurchaseError = (err: Error) => {
    setError(err.message);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in">
        <SectionHeader
          eyebrow="pricing"
          title="Buy credits"
          description="Each credit pays for one conversion: one piece of text turned into audio."
          className="mb-12"
        />

        {/* Success/Cancel Messages */}
        {purchaseSuccess && (
          <div className="mb-8 p-6 bg-primary/10 border border-primary/20 rounded-sm animate-fade-in">
            <div className="flex items-center gap-3">
              <svg
                className="w-5 h-5 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="font-body text-foreground font-medium">
                Payment received. Your credits have been added.
              </p>
            </div>
          </div>
        )}

        {purchaseCanceled && (
          <div className="mb-8 p-6 bg-muted border border-border rounded-sm animate-fade-in">
            <p className="font-body text-muted-foreground">
              Payment was canceled. You can try again when you're ready.
            </p>
          </div>
        )}

        {/* Current Balance */}
        <Card className="mb-12 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-mono text-xs tracking-wide uppercase text-muted-foreground mb-3">
                Current Balance
              </h3>
              <CreditBalance balance={user?.creditBalance || 0} size="lg" />
            </div>
            <p className="font-body text-sm text-muted-foreground">1 credit = 1 conversion</p>
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="mb-8 p-6 bg-destructive/10 border border-destructive/20 rounded-sm animate-fade-in">
            <p className="font-body text-destructive">{error}</p>
          </div>
        )}

        {/* Credit Packs */}
        <div className="mb-12 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          <SectionHeader
            eyebrow="packs"
            title="Choose a credit pack"
            level="h2"
            size="md"
            className="mb-6"
          />

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : packs.length === 0 ? (
            <p className="font-body text-muted-foreground text-center py-8">
              No credit packs available at the moment.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
              {packs.map((pack) => (
                <CreditPackCard
                  key={pack.id}
                  pack={pack}
                  onPurchaseError={handlePurchaseError}
                />
              ))}
            </div>
          )}
        </div>

        {/* FAQ */}
        <div className="animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <SectionHeader
            eyebrow="faq · everything you need to know"
            title="Frequently asked questions"
            level="h2"
            size="md"
            className="mb-6 pb-4 border-b border-border"
          />

          <div className="space-y-4">
            <Card>
              <h3 className="font-body font-medium text-foreground mb-2">What is a credit?</h3>
              <p className="font-body text-muted-foreground text-sm leading-relaxed">
                One credit pays for one conversion: noctua turns one piece of text into audio. The credit is used when the conversion starts.
              </p>
            </Card>
            <Card>
              <h3 className="font-body font-medium text-foreground mb-2">Do credits expire?</h3>
              <p className="font-body text-muted-foreground text-sm leading-relaxed">
                Purchased credits never expire. Use them whenever you are ready.
              </p>
            </Card>
            <Card>
              <h3 className="font-body font-medium text-foreground mb-2">
                What if a conversion fails?
              </h3>
              <p className="font-body text-muted-foreground text-sm leading-relaxed">
                If a conversion fails due to a technical issue on our end, the credit is refunded automatically.
              </p>
            </Card>
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex flex-col items-center gap-2 font-body text-sm text-muted-foreground">
            <div className="flex items-center gap-3">
              <span>Secure payments via Stripe</span>
              <span className="w-1 h-1 rounded-full bg-muted-foreground opacity-50" />
              <span>Credits never expire</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
